import * as React from "react";

import Input from "@v1/components/common/designSystem/Input";
import { Badge } from "@v1/components/designSystem/badge/Badge";
import { Combobox } from "@v1/components/designSystem/combobox/Combobox";
import { MenuItem } from "@v1/components/designSystem/menu/MenuItem";
import { BADGE_TRIGGER_OFFSET } from "@v1/components/editor/constants";
import ModifierGroup from "@v1/components/editor/page/element-editor/components/extras/ModifierGroup";
import useApplyComponentAction from "@v1/hooks/useApplyComponentAction";
import { useOverridableInput } from "@v1/hooks/useOverridableInput";
import {
  selectColor,
  selectDraftComponentId,
  selectIconAltText,
  selectPropIconName,
} from "@v1/reducers/core-reducer";
import { selectOpenPopoverId, setOpenPopoverId } from "@v1/reducers/ui-reducer";
import { useEditorDispatch, useEditorSelector } from "@v1/store/store";
import { iconDirectory } from "replo-runtime/store/iconDirectory";
import { isMixedStyleValue } from "replo-runtime/store/utils/mixed-values";

import ModifierLabel from "../extras/ModifierLabel";
import DynamicColorModifier from "./DynamicColorModifier";

const IconModifierRow: React.FC<
  React.PropsWithChildren<{
    label: string;
  }>
> = ({ label, children }) => (
  <div className="flex w-full items-center">
    <ModifierLabel label={label} />
    <div className="min-w-0 flex-1">{children}</div>
  </div>
);

export const IconModifier = () => {
  return (
    <ModifierGroup title="Icons">
      <div className="flex flex-col gap-2">
        <IconModifierRow label="Icon">
          <IconSelectable />
        </IconModifierRow>
        <IconModifierRow label="Alt Text">
          <IconAltTextControl />
        </IconModifierRow>
        <IconModifierRow label="Color">
          <IconColorControl />
        </IconModifierRow>
      </div>
    </ModifierGroup>
  );
};

const IconSelectable: React.FC = () => {
  const applyComponentAction = useApplyComponentAction();
  const value = useEditorSelector(selectPropIconName);
  const Icon = iconDirectory[value]?.Component;
  const iconName = iconDirectory[value]?.displayName;
  const openPopoverId = useEditorSelector(selectOpenPopoverId);
  const dispatch = useEditorDispatch();

  const isDefaultOpen = openPopoverId === "modifier-icon-selector";
  const [isComboboxOpen, setIsComboboxOpen] = React.useState(isDefaultOpen);

  if (!Icon && !isMixedStyleValue(value)) {
    return null;
  }

  const iconOptions = Object.keys(iconDirectory)
    .sort()
    .map((key) => {
      const iconName = iconDirectory[key]?.displayName ?? "Select an icon";
      const Icon = iconDirectory[key]?.Component;
      return {
        value: key,
        label: (
          <MenuItem
            variant="default"
            size="sm"
            selected={value === key}
            startEnhancer={
              <Badge
                type="icon"
                icon={<Icon />}
                UNSAFE_className="bg-primary text-xs text-white p-0.5"
              />
            }
          >
            {iconName}
          </MenuItem>
        ),
        displayValue: iconName,
        isSelectable: true,
        isDefaultActive: value === key,
      };
    });
  return (
    <Combobox.Root
      options={iconOptions}
      open={isComboboxOpen}
      onOpenChange={(newValue) => {
        // NOTE (Sebas, 2024-05-02): Once the popover is closed, we should reset the open popover state
        // to prevent from opening it again automatically.
        if (isDefaultOpen) {
          dispatch(setOpenPopoverId(null));
        }
        setIsComboboxOpen(newValue);
      }}
      onChange={(value) => {
        applyComponentAction({
          type: "setProps",
          value: { iconName: value },
        });
      }}
    >
      <Combobox.Trigger>
        <Combobox.SelectionButton
          size="sm"
          layoutClassName="flex-1"
          startEnhancer={
            isMixedStyleValue(value) ? (
              <Badge type="unknown" />
            ) : (
              <Badge
                type="icon"
                icon={<Icon />}
                UNSAFE_className="bg-primary p-1 text-white"
              />
            )
          }
          isPlaceholder={false}
          title={
            isMixedStyleValue(value) ? "Mixed" : iconName ?? "Select an icon"
          }
          titleAlignment="start"
        />
      </Combobox.Trigger>
      <Combobox.Popover side="left" align="center" layoutClassName="w-[200px]">
        <Combobox.Content title="Icons" areOptionsSearchable />
      </Combobox.Popover>
    </Combobox.Root>
  );
};

const IconAltTextControl: React.FC = () => {
  const iconAltText = useEditorSelector(selectIconAltText);
  const applyComponentAction = useApplyComponentAction();

  const handleChange = React.useCallback(
    (value: string) => {
      return applyComponentAction({
        type: "setStyles",
        value: { __iconAltText: value },
      });
    },
    [applyComponentAction],
  );

  const inputProps = useOverridableInput({
    value: !isMixedStyleValue(iconAltText) ? iconAltText ?? "" : "Mixed",
    onValueChange: handleChange,
  });

  return <Input placeholder="Icon alt text" {...inputProps} />;
};

const IconColorControl: React.FC = () => {
  const color = useEditorSelector(selectColor);
  const applyComponentAction = useApplyComponentAction();
  const draftComponentId = useEditorSelector(selectDraftComponentId);

  if (!draftComponentId) {
    return null;
  }

  return (
    <DynamicColorModifier
      previewProperty="color"
      popoverTitle="Icon Color"
      gradientSelectionType={null}
      field="style.color"
      value={color ?? "#000000"}
      onChange={(value: string | null) => {
        applyComponentAction({
          type: "setStyles",
          value: { color: value },
        });
      }}
      onRemove={() => {
        applyComponentAction({
          type: "setStyles",
          value: { color: "" },
        });
      }}
      popoverSideOffset={BADGE_TRIGGER_OFFSET}
      showSavedStyles
    />
  );
};
